/**
 * Mock handlers for contacts service
 * Simulates backend API responses with in-memory state persistence
 */

import {
  getContactsState,
  setContactsState,
  getNextContactId,
} from '../data/contacts';
import { getRandomDelay } from '../config';

/**
 * Simulate async delay for realistic network behavior
 * @param {number} ms - Milliseconds to delay
 * @returns {Promise}
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// ============================================================
// CONTACT CRUD OPERATIONS
// ============================================================

/**
 * Mock: GET /api/contacts/contacts/
 * @param {Object} params - Query parameters (search, company, etc.)
 * @returns {Promise<{data: Array}>}
 */
export const mockGetContacts = async (params = {}) => {
  await delay(getRandomDelay());

  let contacts = [...getContactsState()];

  // Apply search filter
  if (params.search) {
    const searchLower = params.search.toLowerCase();
    contacts = contacts.filter(c =>
      c.first_name?.toLowerCase().includes(searchLower) ||
      c.last_name?.toLowerCase().includes(searchLower) ||
      c.email?.toLowerCase().includes(searchLower) ||
      c.company?.toLowerCase().includes(searchLower)
    );
  }

  // Apply company filter
  if (params.company) {
    const companyLower = params.company.toLowerCase();
    contacts = contacts.filter(c =>
      c.company?.toLowerCase().includes(companyLower)
    );
  }

  // Sort by updated_at descending
  contacts.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

  return { data: contacts };
};

/**
 * Mock: GET /api/contacts/contacts/{id}/
 * @param {number|string} id - Contact ID
 * @returns {Promise<{data: Object}>}
 */
export const mockGetContact = async (id) => {
  await delay(getRandomDelay());

  const contact = getContactsState().find(c => c.id === parseInt(id));

  if (!contact) {
    throw {
      response: {
        status: 404,
        data: { detail: 'Contact not found' }
      }
    };
  }

  return { data: contact };
};

/**
 * Mock: POST /api/contacts/contacts/
 * @param {Object} data - Contact data
 * @returns {Promise<{data: Object}>}
 */
export const mockCreateContact = async (data) => {
  await delay(getRandomDelay());

  const contacts = getContactsState();
  const newId = getNextContactId();
  const now = new Date().toISOString();

  const newContact = {
    id: newId,
    first_name: data.first_name || '',
    last_name: data.last_name || '',
    email: data.email || '',
    phone: data.phone || '',
    company: data.company || '',
    position: data.position || '',
    notes: data.notes || '',
    created_at: now,
    updated_at: now,
  };

  setContactsState([...contacts, newContact]);
  return { data: newContact };
};

/**
 * Mock: PUT /api/contacts/contacts/{id}/
 * @param {number|string} id - Contact ID
 * @param {Object} data - Updated contact data
 * @returns {Promise<{data: Object}>}
 */
export const mockUpdateContact = async (id, data) => {
  await delay(getRandomDelay());

  const contacts = getContactsState();
  const contactIndex = contacts.findIndex(c => c.id === parseInt(id));

  if (contactIndex === -1) {
    throw {
      response: {
        status: 404,
        data: { detail: 'Contact not found' }
      }
    };
  }

  const existingContact = contacts[contactIndex];
  const now = new Date().toISOString();

  const updatedContact = {
    ...existingContact,
    first_name: data.first_name !== undefined ? data.first_name : existingContact.first_name,
    last_name: data.last_name !== undefined ? data.last_name : existingContact.last_name,
    email: data.email !== undefined ? data.email : existingContact.email,
    phone: data.phone !== undefined ? data.phone : existingContact.phone,
    company: data.company !== undefined ? data.company : existingContact.company,
    position: data.position !== undefined ? data.position : existingContact.position,
    notes: data.notes !== undefined ? data.notes : existingContact.notes,
    updated_at: now,
  };

  const newContacts = [...contacts];
  newContacts[contactIndex] = updatedContact;
  setContactsState(newContacts);

  return { data: updatedContact };
};

/**
 * Mock: PATCH /api/contacts/contacts/{id}/
 * @param {number|string} id - Contact ID
 * @param {Object} data - Partial contact data
 * @returns {Promise<{data: Object}>}
 */
export const mockPatchContact = async (id, data) => {
  // PATCH is same as PUT for our mock
  return mockUpdateContact(id, data);
};

/**
 * Mock: DELETE /api/contacts/contacts/{id}/
 * @param {number|string} id - Contact ID
 * @returns {Promise<{data: null}>}
 */
export const mockDeleteContact = async (id) => {
  await delay(getRandomDelay());

  const contacts = getContactsState();
  const newContacts = contacts.filter(c => c.id !== parseInt(id));

  if (contacts.length === newContacts.length) {
    throw {
      response: {
        status: 404,
        data: { detail: 'Contact not found' }
      }
    };
  }

  setContactsState(newContacts);
  return { data: null };
};

// ============================================================
// EXPORT ALL HANDLERS
// ============================================================

export default {
  mockGetContacts,
  mockGetContact,
  mockCreateContact,
  mockUpdateContact,
  mockPatchContact,
  mockDeleteContact,
};
